<template>
  <project-manager-layout :spinning="isLoading">
    <leader-project-layout :model="model">
      <template #underInfo>
        <div class="leader-project__common-progress" v-if="model.id">
          <progress-bar-generated
            :show-label="true"
            :show-percent="true"
            :completed="model.completedCount"
            :completed-overdue="model.completedOverdueCount"
            :incomplete-overdue="model.incompleteOverdueCount"
            :incomplete="model.incompleteCount"
          />
        </div>
      </template>
      <div class="leader-project-agreement" v-if="projectAgreementIsVisible">
        <div v-if="showRejectedApprovals" class="rejected_reasons">
          <h3>Закрытие было отклонено</h3>
          <a-collapse
            v-model:activeKey="activeKey"
            :accordion="false"
            :bordered="false"
            style="background: rgb(255, 255, 255)"
          >
            <a-collapse-panel key="1" header="Посмотреть причины">
              <template
                v-for="(approval, index) in getRejectedApprovals()"
                :key="`approval-${index}`"
              >
                <agreement-row
                  class="agreement-row"
                  :status="approval.status"
                  :reason="approval.rejectedReason"
                  :action="() => handleApproval(approval, index)"
                  :action-button-text="'Согласовать'"
                  :loading="approvalLoaders[index]"
                >
                  <template #icon>
                    <img
                      v-if="approval.role === USER_ROLE_PARTNER"
                      src="@/assets/icons/icon-person.svg"
                      alt=""
                    />
                    <img v-else src="@/assets/icons/icon-bag.svg" alt="" />
                  </template>

                  <template #labelText>
                    <div v-if="approval.role === USER_ROLE_PARTNER">
                      Партнер отправил проект на доработку
                    </div>
                    <div v-else>
                      Администратор отправил проект на доработку<br />
                      Портфель: {{ approval.briefcaseName }}
                    </div>
                  </template>
                </agreement-row>
              </template>
            </a-collapse-panel>
          </a-collapse>
        </div>

        <a-form
          class="form"
          :model="report"
          :rules="rules"
          @validate="errors.onValidate"
          @finish="handleCloseProject"
        >
          <template v-if="!reportIsClosed">
            <h3>Закрыть проект</h3>
            <div class="description">
              Выберите основание для закрытия проекта и напишите комментарий при
              необходимости. Обратите внимание, что для закрытия проекта
              необходимо предварительно заполнить отчет. После закрытия проекта
              Вы не сможете редактировать отчет, и он станет доступен партнеру и
              администратору. Пока проект открыт, Вы можете вносить изменения.
            </div>
            <a-form-item
              name="closeReasonId"
              :help="errors.getError('closeReasonId')"
              :validate-status="errors.getStatus('closeReasonId')"
            >
              <slot>
                <h4>Основание для закрытия проекта *</h4>
              </slot>
              <a-select
                v-model:value="report.closeReasonId"
                placeholder="Выберите основание"
                :options="closeReasonList"
                :disabled="closeReasonList.length === 0"
                @select="onReasonSelect"
              />
            </a-form-item>
            <a-form-item
              class="comment"
              name="closeComment"
              :help="errors.getError('closeComment')"
              :validate-status="errors.getStatus('closeComment')"
            >
              <slot>
                <h4>Комментарий</h4>
              </slot>
              <a-textarea
                v-model:value="report.closeComment"
                placeholder="Напишите комментарий при необходимости"
                :autoSize="{ minRows: 8, maxRows: 8 }"
                style="padding: 10px"
              />
            </a-form-item>
          </template>
          <template v-else>
            <h3>Согласование</h3>

            <div class="description">
              <p>
                Согласуйте завершение проекта и отчет. Вы можете отправить на
                согласование одновременно администратору и партнеру или выбрать
                нужную Вам очередность согласования.
              </p>
              <p>
                <span>Основание для закрытия:&nbsp;</span
                >{{ report.closeReason }}
              </p>
              <p><span>Комментарий:&nbsp;</span>{{ report.closeComment }}</p>
            </div>

            <div class="project-agreement-grid">
              <template
                v-for="(approval, index) in model.approvals"
                :key="`approval-${index}`"
              >
                <agreement-row
                  class="agreement-row"
                  :status="approval.status"
                  :reason="approval.rejectedReason"
                  :action="() => handleApproval(approval, index)"
                  :reapprove="() => reApprove(approval, index)"
                  :action-button-text="'Согласовать'"
                  :loading="approvalLoaders[index]"
                  :error="approvalErrors[index].getError('approval')"
                >
                  <template #icon>
                    <img
                      v-if="approval.role === USER_ROLE_PARTNER"
                      src="@/assets/icons/icon-person.svg"
                      alt=""
                    />
                    <img v-else src="@/assets/icons/icon-bag.svg" alt="" />
                  </template>

                  <template #labelText>
                    <div v-if="approval.status?.type === 'rejected'">
                      <div v-if="approval.role === USER_ROLE_PARTNER">
                        Партнер отправил проект на доработку
                      </div>
                      <div v-else>
                        Администратор отправил проект на доработку<br />
                        Портфель: {{ approval.briefcaseName }}
                      </div>
                    </div>
                    <div v-else>
                      {{ getActionText(approval) }}
                    </div>
                  </template>
                </agreement-row>
              </template>
            </div>
          </template>

          <div v-if="store.getters.enableCompetencies">
            <a-divider />

            <project-competencies
              ref="projectCompetenciesRef"
              :title="'Копетенции проекта'"
            />
            <a-divider />

            <leader-my-competencies
              :project-status-id="model.projectStatusId"
            />
          </div>

          <template v-if="!reportIsClosed">
            <a-form-item class="actions">
              <c-button-primary
                :disabled="reportIsClosed || closeReasonList.length === 0"
                :loading="submitButtonIsLoading"
                html-type="submit"
              >
                Закрыть проект
              </c-button-primary>
            </a-form-item>
          </template>
        </a-form>
      </div>
      <c-drawer v-model:open="showWarningOnClose" destroyOnClose>
        <component
          :is="LeaderProjectAgreementWarning"
          @close="showWarningOnClose = !showWarningOnClose"
          @continue="onFinish"
          @edit="onEditShowcase"
        />
      </c-drawer>
    </leader-project-layout>
  </project-manager-layout>
</template>

<script setup>
  import { computed, onMounted, reactive, ref } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useApi } from '@/api/use-api';
  import ProjectManagerLayout from '@/layouts/ProjectManagerLayout.vue';
  import LeaderProjectLayout from '@/views/leader/projects/components/LeaderProjectLayout.vue';
  import CButtonPrimary from '@/components/UI/CButtonPrimary';
  import ErrorsHandler from '@/components/form/errors-handler';
  import AgreementRow from '@/components/projects/agreement/AgreementRow';
  import {
    REASON_DONE,
    REASON_NOT_DONE_FROM_CONTRACTOR,
    REASON_NOT_DONE_FROM_PARTNER,
  } from '@/api/models/ProjectCloseReason';
  import { USER_ROLE_LEADER, USER_ROLE_PARTNER } from '@/api/models/UserRole';
  import {
    PROJECT_STATUS_ACCEPTANCE,
    PROJECT_STATUS_AGREEMENT,
    PROJECT_STATUS_CLOSED,
    PROJECT_STATUS_DRAFT,
    PROJECT_STATUS_IN_WORK,
  } from '@/api/models/ProjectStatus';
  import ProgressBarGenerated from '@/components/projects/portfolio/components/ProgressBarGenerated.vue';
  import ProjectCompetencies from '@/components/projects/competencies/ProjectCompetencies.vue';
  import LeaderMyCompetencies from '@/views/leader/projects/agreement/my-competencies/LeaderMyCompetencies.vue';
  import { useStore } from 'vuex';
  import { TEXT_LENGTH_ERROR } from '@/constants/error';
  import LeaderProjectAgreementWarning from '@/views/leader/projects/agreement/LeaderProjectAgreementWarning.vue';
  import CDrawer from '@/components/UI/CDrawer.vue';

  const api = useApi();
  const route = useRoute();
  const router = useRouter();
  const store = useStore();
  const projectCompetenciesRef = ref(null);
  const approvalErrors = ref([]);
  const approvalLoaders = ref([]);
  const activeKey = ref(null);
  const isLoading = ref(true);
  const submitButtonIsLoading = ref(false);
  const showWarningOnClose = ref(false);
  const publicShowcase = ref(null);

  const model = reactive({
    id: null,
    isExternal: true,
    isMyProject: false,
    number: '',
    name: '',
    projectType: '',
    requestId: null,
    status: null,
    projectStatusId: null,
    approvals: [],
    allIndexesCompleted: false,
    completedCount: 0,
    completedOverdueCount: 0,
    incompleteOverdueCount: 0,
    incompleteCount: 0,
  });

  const getActionText = (approval) => {
    return approval.role === USER_ROLE_PARTNER
      ? 'Согласовать завершение проекта с партнером'
      : `Согласовать завершение проекта с администратором портфеля: ${approval.briefcaseName}`;
  };

  const getRejectedApprovals = () => {
    return model.approvals.filter((item) => item.status?.type === 'rejected');
  };

  const isAllTasksCompleted = computed(() => {
    return model.incompleteCount + model.incompleteOverdueCount === 0;
  });

  const closeReasonList = computed(() => {
    const reasons = [
      { value: REASON_DONE, label: 'Проект успешно выполнен' },
      {
        value: REASON_NOT_DONE_FROM_CONTRACTOR,
        label: 'Проект не выполнен, закрыт исполнителем',
      },
      {
        value: REASON_NOT_DONE_FROM_PARTNER,
        label: 'Проект не выполнен, закрыт заказчиком',
      },
    ];
    return isAllTasksCompleted.value && model.allIndexesCompleted
      ? reasons
      : reasons.slice(1);
  });
  const projectAgreementIsVisible = computed(
    () =>
      ![PROJECT_STATUS_DRAFT, PROJECT_STATUS_AGREEMENT].includes(
        model.projectStatusId
      )
  );
  const reportIsClosed = computed(
    () =>
      model.projectStatusId === PROJECT_STATUS_ACCEPTANCE ||
      model.projectStatusId === PROJECT_STATUS_CLOSED
  );
  const showRejectedApprovals = computed(
    () =>
      model.projectStatusId === PROJECT_STATUS_IN_WORK &&
      model.approvals.some((item) => item.status?.type === 'rejected')
  );

  const report = reactive({
    closeReasonId: null,
    closeComment: null,
  });

  const rules = {
    closeReasonId: [
      {
        required: true,
        message: 'Необходимо указать основание для закрытия проекта',
        trigger: 'change',
      },
    ],
    closeComment: [
      {
        type: 'string',
        required: false,
        min: 2,
        max: 5000,
        message: TEXT_LENGTH_ERROR,
        trigger: 'change',
      },
    ],
  };

  const errors = new ErrorsHandler(
    {
      closeReasonId: '',
      closeComment: '',
    },
    // Ошибку роли выводим под селектором причины закрытия
    (fieldName) => {
      if ('role' === fieldName) {
        return 'closeReasonId';
      }
      return fieldName;
    }
  );

  onMounted(async () => {
    await getModel();
    await getReport();
    isLoading.value = false;
  });

  const getModel = async () => {
    try {
      const response = await api.leaderPage.getProjectToClose(route.params.id);
      model.name = response.project.name;
      model.isExternal = response.project.isExternal;
      model.isMyProject = response.project.isMyProject;
      model.number = response.project.id;
      model.id = response.project.id;
      model.type = response.project.type;
      model.requestId = response.project.requestId;
      model.status = response.project.status;
      model.projectStatusId = response.project.projectStatusId;
      model.approvals = response.project.approvals;
      model.allIndexesCompleted = response.project.allIndexesCompleted;
      model.completedCount = response.project.completedCount;
      model.completedOverdueCount = response.project.completedOverdueCount;
      model.incompleteOverdueCount = response.project.incompleteOverdueCount;
      model.incompleteCount = response.project.incompleteCount;

      approvalErrors.value = model.approvals.map(() => {
        return new ErrorsHandler({ approval: '' });
      });
      approvalLoaders.value = model.approvals.map(() => false);
    } catch (error) {
      console.error('error', error);
    }
  };

  const getReport = async () => {
    try {
      const response = await api.projectReport.view(route.params.id);
      report.closeReasonId = response.report.isClosed
        ? response.report.closeReasonId
        : null;
      report.closeReason = response.report.closeReason;
      report.closeComment = response.report.closeComment;
    } catch (error) {
      console.error('error', error);
    }
  };

  const handleCloseProject = async () => {
    await getPublicShowcase();
    if (publicShowcase.value) {
      showWarningOnClose.value = true;
    } else {
      await onFinish();
    }
  };

  const onFinish = async () => {
    try {
      submitButtonIsLoading.value = true;
      await api.projectReport.closeReport({
        projectId: route.params.id,
        role: USER_ROLE_LEADER,
        closeReasonId: report.closeReasonId,
        closeComment: report.closeComment,
      });
      await getModel();
    } catch (error) {
      errors.handleApiErrors(error);
    } finally {
      submitButtonIsLoading.value = false;
    }
  };

  const handleApproval = async (approval, index) => {
    try {
      approvalLoaders.value[index] = true;
      approvalErrors.value[index].clearErrors();
      await api.leaderPage.createProjectApproval({
        projectId: model.id,
        role: approval.role,
        briefcaseProjectId: approval.briefcaseProjectId,
      });
      await getModel();
    } catch (error) {
      approvalErrors.value[index].handleApiErrors(error);
    } finally {
      approvalLoaders.value[index] = false;
    }
  };

  const reApprove = async (approval, index) => {
    try {
      approvalLoaders.value[index] = true;
      approvalErrors.value[index].clearErrors();
      await api.leaderPage.reApproveProject({
        projectApprovalId: approval.id,
      });
      await getModel();
    } catch (error) {
      approvalErrors.value[index].handleApiErrors(error);
    } finally {
      approvalLoaders.value[index] = false;
    }
  };

  const onReasonSelect = (reasonId) => {
    report.closeReasonId = reasonId;
  };

  const getPublicShowcase = async () => {
    try {
      submitButtonIsLoading.value = true;
      const response = await api.showcase.getPublic(route.params.id);
      publicShowcase.value = response.showcase;
    } catch (error) {
      console.log('error', error);
    } finally {
      submitButtonIsLoading.value = false;
    }
  };

  const onEditShowcase = async () => {
    await router.push(`/leader/project/${route.params.id}/showcase`);
  };
</script>

<style lang="scss" scoped>
  @use "@/assets/styles/_colors.scss" as *;

  * {
    margin: 0;
    padding: 0;
  }

  :deep(.ant-collapse-header) {
    padding: 0 0 12px 0 !important;
  }

  .leader-project__common-progress {
    margin-top: 42px;
  }

  .leader-project-agreement {
    margin-top: 10px;
    max-width: 902px;

    h3 {
      font-size: 18px;
      margin-bottom: 10px;
    }

    h4 {
      font-size: 14px;
      margin: 30px 0 6px;

      &.disabled {
        color: $color-bg-status-new !important;
      }
    }

    .rejected_reasons {
      margin-bottom: 30px;
    }

    .description {
      color: $color-bg-status-new;
      display: flex;
      flex-direction: column;
      font-family: 'Nunito Sans', sans-serif;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      gap: 18px;
      line-height: 20px;
      max-width: 678px;
    }

    .project-agreement-grid {
      margin-top: 10px;
      max-width: 902px;
      padding-bottom: 100px;

      .agreement-row {
        margin-top: 30px;
      }
    }

    .attachments {
      margin-top: 14px;
    }

    .statuses {
      display: flex;
      flex-direction: column;
      gap: 14px;
      margin-top: 30px;
    }

    .actions {
      text-align: right;
      margin-top: 26px;
    }
  }

  .ant-divider {
    margin: 40px 0;
  }
</style>
